import { Link, useNavigate } from 'react-router-dom';
import { Building2, IndianRupee, Receipt, Search, Wallet, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import {
  SUBSCRIPTION_PAYMENT_METHOD_LABEL,
  SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
  type SubscriptionPaymentMethod,
} from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '@/lib/format';
import type { SubscriptionTransaction } from '@/types/institute';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { subscriptionTransactionsApi } from '../api/subscriptionTransactions.api';

const PAGE_SIZE = 20;

const METHOD_FILTER = [
  { value: '', label: 'Every method' },
  ...SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
];

/** The screen's filters, as they sit in the address bar. */
type Filters = {
  page: number;
  search: string;
  method: SubscriptionPaymentMethod | '';
  periodYear: string;
  from: string;
  to: string;
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  search: '',
  method: '',
  periodYear: '',
  from: '',
  to: '',
};

/** A method in a pasted link that is not a real one shows every method instead. */
const FILTER_OPTIONS = {
  allowed: {
    method: Object.keys(SUBSCRIPTION_PAYMENT_METHOD_LABEL),
  },
};

/** `2026-27`. Anything else is still being typed, so it is not sent yet. */
const LICENCE_YEAR = /^\d{4}-\d{2}$/;

const COLUMNS: Column<SubscriptionTransaction>[] = [
  {
    key: 'receipt',
    header: 'Receipt',
    render: (row) => (
      <div className="min-w-0">
        <p className="font-mono text-xs font-[650] text-ink">{row.receiptNumber}</p>
        <p className="text-xs text-ink-3">Licence {row.periodYear}</p>
      </div>
    ),
  },
  {
    key: 'institute',
    header: 'Institute',
    render: (row) => (
      <div className="min-w-0">
        <Link
          to={`/platform/institutes/${row.institute.uuid}/subscription`}
          // The row opens the institute's ledger too; this keeps the click from firing twice.
          onClick={(event) => event.stopPropagation()}
          className="truncate rounded-field font-[560] text-ink transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        >
          {row.institute.displayName}
        </Link>
        <p className="truncate text-xs text-ink-3">
          <span className="font-mono uppercase">{row.institute.code}</span> · {row.institute.city}
        </p>
      </div>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (row) => (
      <span className="font-[650] tabular-nums text-ink">{formatCurrency(row.amount)}</span>
    ),
  },
  {
    key: 'paid',
    header: 'Paid on',
    render: (row) => (
      <div>
        <p className="text-ink">{formatDate(row.paidOn)}</p>
        <p className="text-xs text-ink-3">{SUBSCRIPTION_PAYMENT_METHOD_LABEL[row.method]}</p>
      </div>
    ),
  },
  {
    key: 'reference',
    header: 'Reference',
    hideOnMobile: true,
    render: (row) =>
      row.referenceNo ? (
        <span className="font-mono text-xs text-ink-2">{row.referenceNo}</span>
      ) : (
        <span className="text-ink-3">—</span>
      ),
  },
  {
    key: 'renews',
    header: 'Renews on',
    hideOnMobile: true,
    render: (row) => <span className="text-ink-2">{formatDate(row.renewsOn)}</span>,
  },
  {
    key: 'recorded',
    header: 'Recorded',
    hideOnMobile: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="truncate text-ink-2">{row.recordedBy ?? 'Unknown'}</p>
        <p className="text-xs text-ink-3">{formatDateTime(row.recordedAt)}</p>
      </div>
    ),
  },
  {
    key: 'note',
    header: 'Note',
    hideOnMobile: true,
    render: (row) =>
      row.note ? (
        <p className="max-w-[220px] truncate text-xs text-ink-2" title={row.note}>
          {row.note}
        </p>
      ) : (
        <span className="text-ink-3">—</span>
      ),
  },
];

/**
 * Every subscription payment from every customer, one row per transaction.
 *
 * The dashboard says how much came in; this is where somebody checks which
 * payment it was — the receipt number, the UTR, who entered it and when. Each
 * row opens that institute's own ledger, where receipts are printed and new
 * payments are recorded. The totals above the list cover every payment the
 * filters match, not only the page on screen.
 */
export function SubscriptionTransactionsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { page, search, method, periodYear, from, to } = filters;
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const year = LICENCE_YEAR.test(periodYear.trim()) ? periodYear.trim() : '';

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      subscriptionTransactionsApi.list({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        method: method || undefined,
        periodYear: year || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    [page, debouncedSearch, method, year, from, to],
  );

  const isFiltered = Boolean(debouncedSearch || method || year || from || to);
  const summary = data?.summary;

  /** Every filter change starts again from the first page. */
  function updateFilter(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  function clearFilters() {
    updateFilter({ search: '', method: '', periodYear: '', from: '', to: '' });
  }

  return (
    <>
      <DeckPageHeader
        eyebrow="Subscriptions"
        title="Subscription payments"
        description="Every licence payment from every institute — newest first."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricTile
          label="Received"
          icon={<IndianRupee className="h-4 w-4" />}
          tone="emerald"
          isLoading={!summary}
          value={formatCurrency(summary?.totalPaid ?? '0')}
          hint={isFiltered ? 'For these filters' : 'All time, every institute'}
        />
        <MetricTile
          label="Payments"
          icon={<Receipt className="h-4 w-4" />}
          tone="indigo"
          isLoading={!summary}
          value={formatNumber(summary?.paymentCount ?? 0)}
          hint="Transactions recorded"
        />
        <MetricTile
          label="Institutes"
          icon={<Building2 className="h-4 w-4" />}
          tone="sky"
          isLoading={!summary}
          value={formatNumber(summary?.instituteCount ?? 0)}
          hint="That these payments came from"
        />
      </div>

      <section className="card flex flex-col gap-3 p-4" aria-labelledby="payment-filters-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="payment-filters-heading" className="lbl">
            Filter payments
          </h2>
          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<X className="h-4 w-4" />}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            label="Search"
            placeholder="Institute, code, receipt or UTR"
            leftIcon={<Search className="h-4 w-4" />}
            value={search}
            onChange={(event) => updateFilter({ search: event.target.value })}
          />
          <Select
            label="Method"
            options={METHOD_FILTER}
            value={method}
            onChange={(event) =>
              updateFilter({ method: event.target.value as SubscriptionPaymentMethod | '' })
            }
          />
          <Input
            label="Licence year"
            placeholder="2026-27"
            inputMode="numeric"
            maxLength={7}
            value={periodYear}
            error={periodYear.trim() && !year ? 'Use a year like 2026-27' : undefined}
            onChange={(event) => updateFilter({ periodYear: event.target.value })}
          />
          <DatePicker
            label="Paid from"
            value={from}
            max={to || undefined}
            onChange={(event) => updateFilter({ from: event.target.value })}
          />
          <DatePicker
            label="Paid to"
            value={to}
            min={from || undefined}
            onChange={(event) => updateFilter({ to: event.target.value })}
          />
        </div>
      </section>

      {error ? (
        <QueryError title="Could not load subscription payments" error={error} onRetry={refetch} />
      ) : (
        <div className="card overflow-hidden">
          <Table
            variant="plain"
            columns={COLUMNS}
            rows={data?.items ?? []}
            rowKey={(row) => row.uuid}
            isLoading={isLoading}
            onRowClick={(row) =>
              navigate(`/platform/institutes/${row.institute.uuid}/subscription`)
            }
            empty={
              <EmptyState
                icon={<Wallet className="h-5 w-5" />}
                title={isFiltered ? 'No payments match' : 'No payments yet'}
                description={
                  isFiltered
                    ? 'Nothing matches these filters. Clear them to see every payment.'
                    : 'When a payment is recorded against an institute, it appears here.'
                }
              />
            }
          />
          {data && (
            <Pagination
              meta={data.meta}
              onPageChange={(next) => updateFilter({ page: next })}
              itemLabel="payments"
            />
          )}
        </div>
      )}
    </>
  );
}
